"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import { useRouter } from "next/navigation"
import { useAuth } from "@/hooks/use-auth"
import { useTranslatedErrorMessage } from "@/hooks/use-translated-error-message"
import { updateMyProfile, type UpdateMyProfileInput } from "@/server-actions/user-profile"
import { withQueryHandling } from "@/lib/with-query-handling"

export function useUpdateMyProfile() {
  const t = useTranslations("UserProfile")
  const common = useTranslations("Common")
  const errors = useTranslatedErrorMessage()
  const { refreshAuth } = useAuth()
  const router = useRouter()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateMyProfileInput) => withQueryHandling(saveProfile(input), {
      loading: common("saving"), showSuccessToast: false, error: t("profileFailed"),
    }),
    onSuccess: async (_result, input) => {
      await refreshAuth().catch(() => {})
      await queryClient.invalidateQueries({ queryKey: ["users"] })
      if (input.locale) router.refresh()
    },
  })

  async function saveProfile(input: UpdateMyProfileInput) {
    const result = await updateMyProfile(input)
    if (!("status" in result)) return result
    let message: string
    switch (result.errorMessage) {
      case "languageSignIn": message = t("languageSignIn"); break
      case "languageUnavailable": message = t("languageUnavailable"); break
      case "languageFailed": message = t("profileFailed"); break
      default: message = errors.message(result.errorMessage ?? t("profileFailed"))
    }
    return { ...result, errorMessage: message }
  }

}
